import type {
  IntegrationMutationError,
  IntegrationMutationErrorCode,
} from "./types";

/**
 * A change the integrations service turned down. The service answers one as
 * data, in the mutation's `errors`; thrown, it fails the call like any other
 * error, carrying the service's message, which is written for the person who
 * asked and safe to show.
 */
export class IntegrationRefusedError extends Error {
  readonly code: IntegrationMutationErrorCode;

  constructor({ code, message }: IntegrationMutationError) {
    super(message);
    this.name = "IntegrationRefusedError";
    this.code = code;
  }
}

/** Throws the first refusal in a mutation's answer. An answer without one passes. */
export function throwIfRefused(
  result:
    | { errors?: readonly IntegrationMutationError[] | null }
    | null
    | undefined,
): void {
  const refusal = result?.errors?.[0];
  if (refusal) throw new IntegrationRefusedError(refusal);
}

interface GraphQLErrorLike {
  extensions?: { code?: unknown } | null;
}

const graphQLErrorsOf = (error: unknown): GraphQLErrorLike[] => {
  const errors = (error as { graphQLErrors?: unknown } | null)?.graphQLErrors;
  return Array.isArray(errors) ? (errors as GraphQLErrorLike[]) : [];
};

/**
 * The refusal's code, or `FORBIDDEN` when the permission layer turned the
 * caller away: it does that with a GraphQL error, before the service is
 * asked. Null for any other failure.
 */
export function integrationErrorCode(
  error: unknown,
): IntegrationMutationErrorCode | "FORBIDDEN" | null {
  if (error instanceof IntegrationRefusedError) return error.code;
  return graphQLErrorsOf(error).some(
    (entry) => entry.extensions?.code === "FORBIDDEN",
  )
    ? "FORBIDDEN"
    : null;
}

/**
 * The refusal's message, else `fallback`. Nothing else carries a message
 * written for the person: a GraphQL error's describes the fault to whoever
 * debugs it.
 */
export function integrationErrorMessage(
  error: unknown,
  fallback: string,
): string {
  return (
    (error instanceof IntegrationRefusedError && error.message) || fallback
  );
}
